import { useLocation, useParams } from 'react-router-dom';
import { listAs2DlqTasks } from '../api/as2Dlq';
import { getAs2InboundMessage } from '../api/as2InboundMessages';
import { CopyButton } from '../components/CopyButton';
import { PageHeader } from '../components/FeatureStatus';
import { EmptyState, ErrorState, LoadingState } from '../components/RequestState';
import { StatusBadge } from '../components/StatusBadge';
import { RelatedLinks } from '../components/RelatedLinks';
import { Timeline } from '../components/Timeline';
import { useAsyncData } from '../hooks/useAsyncData';
import { findMatchingAs2DlqTask } from '../lib/as2DlqMatch';
import { as2DlqTasksLink, inboundMessagesByAs2MessageIdLink } from '../lib/crossLinks';
import { mergeTimelineEvents } from '../lib/timeline';
import type { As2CertificateSummary } from '../types/api';

// Bounds the client-side DLQ lookup below to the first page of tasks for this message's
// partner — enough to find a match in the normal case without an unbounded fetch. No taskType
// filter is applied here (unlike the outbound page) because an inbound message's DLQ task can
// be either INBOUND_ASYNC_MDN_DELIVERY or INBOUND_CALLBACK; matching by referenceId (see
// lib/as2DlqMatch) is exact either way since inbound and outbound messages share one id space.
const DLQ_LOOKUP_SIZE = 200;

// Detail view for a single AS2 gateway inbound message record (transport-level: signature
// validation, decryption, MDN delivery, callback state). There is no payload/body field on
// the backing InboundMessageResponse DTO, so this page intentionally has no Payload tab —
// that content lives on the separate business-level /inbound-messages/:id route.
//
// This record has no correlationId field (see types/api.ts As2InboundMessageRecord), so the
// only reliable cross-link is via as2MessageId into the business-level inbound document list
// (InboundDocumentListQuery.as2MessageId). See src/lib/crossLinks.ts and the cross-link gap
// note in the PR description for why this direction can't also reach Exchanges/Executions.

export function As2InboundMessageDetailPage() {
  const params = useParams<{ id: string }>();
  const { search } = useLocation();
  const messageId = params.id ?? '';
  const { data, isLoading, error, refresh } = useAsyncData(() => getAs2InboundMessage(messageId), [messageId]);
  const { data: dlqTasks } = useAsyncData(
    () => (data
      ? listAs2DlqTasks({ partnerCode: data.partnerCode, size: DLQ_LOOKUP_SIZE })
      : Promise.resolve({ data: [], total: 0, page: 0, pageSize: 0 })),
    [data?.id, data?.partnerCode],
  );

  if (isLoading) return <LoadingState label="Loading AS2 inbound message..." />;
  if (error) return <ErrorState message={error} onRetry={refresh} />;
  if (!data) return null;

  const matchingDlqTask = findMatchingAs2DlqTask(dlqTasks?.data ?? [], data.id);

  const timeline = mergeTimelineEvents([
    { id: 'received', label: 'Received', at: data.receivedAt, tone: 'neutral' },
    { id: 'mdn-sent', label: 'MDN sent', at: data.mdnSentAt, tone: 'success' },
    { id: 'mdn-recorded', label: 'MDN recorded', at: data.mdn?.receivedAt, tone: 'success' },
    { id: 'last-retry', label: 'Last retry attempt', at: data.retry?.lastRetryAt, tone: 'warning' },
    { id: 'next-retry', label: 'Next retry scheduled', at: data.retry?.nextRetryAt, tone: 'warning' },
    { id: 'dead-lettered', label: 'Dead-lettered', at: data.retry?.deadLetteredAt, tone: 'error' },
  ]);

  return (
    <div className="page-grid">
      <PageHeader
        eyebrow="AS2 Gateway"
        title="AS2 inbound message (transport)"
        purpose="Transport-level detail for a single inbound AS2 receipt: signature/decryption checks, MDN delivery, and retry history."
        status="PRODUCTION_READY"
        connected
        next="No payload is stored on this transport record. Use Messages > Inbound Documents for the parsed business-level content."
      />

      <section className="panel">
        <div className="panel-header">
          <div>
            <h2>{data.as2MessageId ?? data.id}</h2>
            <span className="inline-copy">
              <span className="mono" title={data.id}>{data.id}</span>
              <CopyButton value={data.id} label="Copy message ID" />
            </span>
          </div>
          <StatusBadge value={data.status} />
        </div>
        <div className="summary-grid">
          <div><strong>Partner code</strong><span>{data.partnerCode ?? 'n/a'}</span></div>
          <div><strong>Content type</strong><span>{data.contentType ?? 'n/a'}</span></div>
          <div><strong>Received at</strong><span>{data.receivedAt ? new Date(data.receivedAt).toLocaleString('en-GB') : 'n/a'}</span></div>
          <div><strong>Callback status</strong><span>{data.callbackStatus ?? 'n/a'}</span></div>
          {data.as2MessageId ? (
            <div>
              <strong>Business inbound document</strong>
              <span className="inline-copy">
                <RelatedLinks links={[inboundMessagesByAs2MessageIdLink(data.as2MessageId, search)]} />
              </span>
            </div>
          ) : null}
          <div><strong>Failure reason</strong><span>{data.failureReason ?? 'n/a'}</span></div>
        </div>
      </section>

      <section className="panel">
        <div className="panel-header">
          <div>
            <h2>Protocol &amp; security</h2>
            <p>Signature validation, decryption, and MIC state recorded for this receipt.</p>
          </div>
        </div>
        <div className="summary-grid">
          <div><strong>MIC value</strong><span className="mono">{data.micValue ?? 'n/a'}</span></div>
          <div><strong>MIC algorithm</strong><span>{data.micAlgorithm ?? 'n/a'}</span></div>
          <div><strong>Signed</strong><span>{data.signed ? 'Yes' : 'No'}</span></div>
          <div><strong>Signature valid</strong><span>{data.signatureValid === undefined ? 'n/a' : data.signatureValid ? 'Yes' : 'No'}</span></div>
          <div><strong>Encrypted</strong><span>{data.encrypted ? 'Yes' : 'No'}</span></div>
          <div><strong>Security processing status</strong><span>{data.securityProcessingStatus ?? 'n/a'}</span></div>
        </div>
      </section>

      <section className="panel">
        <div className="panel-header">
          <div>
            <h2>MDN delivery</h2>
            <p>Message disposition notification sent back to the partner for this receipt.</p>
          </div>
        </div>
        <div className="summary-grid">
          <div><strong>MDN mode</strong><span>{data.mdnMode ?? 'n/a'}</span></div>
          <div><strong>MDN delivery status</strong><span>{data.mdnDeliveryStatus ?? 'n/a'}</span></div>
          <div><strong>MDN delivery URL</strong><span className="mono">{data.mdnDeliveryUrl ?? 'n/a'}</span></div>
          <div><strong>MDN sent at</strong><span>{data.mdnSentAt ? new Date(data.mdnSentAt).toLocaleString('en-GB') : 'n/a'}</span></div>
          <div><strong>MDN signature status</strong><span>{data.mdnSignatureStatus ?? 'n/a'}</span></div>
          <div><strong>MDN failure reason</strong><span>{data.mdnFailureReason ?? 'n/a'}</span></div>
        </div>
        {data.mdn ? (
          <div className="panel-subsection">
            <h3>MDN record</h3>
            <div className="summary-grid">
              <div><strong>Disposition</strong><span>{data.mdn.disposition ?? 'n/a'}</span></div>
              <div><strong>Received content MIC</strong><span className="mono">{data.mdn.receivedContentMic ?? 'n/a'}</span></div>
              <div><strong>Validation status</strong><span>{data.mdn.validationStatus ?? 'n/a'}</span></div>
              <div><strong>Failure reason</strong><span>{data.mdn.failureReason ?? 'n/a'}</span></div>
              <div><strong>Recorded at</strong><span>{data.mdn.receivedAt ? new Date(data.mdn.receivedAt).toLocaleString('en-GB') : 'n/a'}</span></div>
              {data.mdn.signingCertificate ? (
                <div className="full-width">
                  <strong>Signing certificate</strong>
                  <span>{formatCertificate(data.mdn.signingCertificate)}</span>
                </div>
              ) : null}
            </div>
          </div>
        ) : <EmptyState label="No MDN record is attached to this message yet." />}
      </section>

      <section className="panel">
        <div className="panel-header">
          <div>
            <h2>Retry</h2>
            <p>Retry scheduling state recorded for this inbound message's callback/MDN delivery.</p>
          </div>
          {matchingDlqTask ? (
            <RelatedLinks links={[as2DlqTasksLink(matchingDlqTask.taskType, data.partnerCode, search)]} />
          ) : null}
        </div>
        {data.retry ? (
          <div className="summary-grid">
            <div><strong>Retry state</strong><span>{data.retry.retryState ?? 'n/a'}</span></div>
            <div><strong>Retry count</strong><span>{data.retry.retryCount ?? 0}/{data.retry.maxAttempts ?? 'n/a'}</span></div>
            <div><strong>Retry category</strong><span>{data.retry.retryCategory ?? 'n/a'}</span></div>
            <div><strong>Next retry at</strong><span>{data.retry.nextRetryAt ? new Date(data.retry.nextRetryAt).toLocaleString('en-GB') : 'n/a'}</span></div>
            <div><strong>Last retry at</strong><span>{data.retry.lastRetryAt ? new Date(data.retry.lastRetryAt).toLocaleString('en-GB') : 'n/a'}</span></div>
            <div><strong>Last retry reason</strong><span>{data.retry.lastRetryReason ?? 'n/a'}</span></div>
            <div><strong>Dead-lettered at</strong><span>{data.retry.deadLetteredAt ? new Date(data.retry.deadLetteredAt).toLocaleString('en-GB') : 'n/a'}</span></div>
            <div><strong>Dead-letter reason</strong><span>{data.retry.deadLetterReason ?? 'n/a'}</span></div>
          </div>
        ) : <p>No retry activity recorded for this message.</p>}
      </section>

      {timeline.length > 0 ? (
        <section className="panel">
          <div className="panel-header">
            <h2>Timeline</h2>
          </div>
          <Timeline events={timeline} />
        </section>
      ) : null}
    </div>
  );
}

function formatCertificate(certificate: As2CertificateSummary): string {
  const parts = [certificate.alias, certificate.fingerprintSha256, certificate.validUntil ? `valid until ${new Date(certificate.validUntil).toLocaleDateString('en-GB')}` : undefined];
  return parts.filter(Boolean).join(' - ') || certificate.id;
}
